import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { withLocalize } from 'react-localize-redux';
import Label from '../../components/foundations/Label';
import AgencyIndicator from '../../components/molecules/AgencyIndicator';
import VerticalList from '../../components/partials/VerticalList/VerticalList';
import DefaultIcon from '../../resources/svg/icon_partner.svg';
import { trackMixpanelEvent } from '../../utils/general';
import { sortAlphabetically } from '../../utils/sortUtil';
import { GET_USER_FAVOURITE_PARTNERS, UPDATE_USER_FAVOURITE_PARTNER } from '../queries';
import css from './PartnerList.css';

const translationPrefix = 'STATIC.PAGES.PARTNER_CONFIGURATION.PARNTERS_LIST';

const PartnersList = ({
    partners = [],
    selectedPartner = null,
    filter = '',
    className = '',
    onPartnerSelected = () => {},
    sortPartners = partnersList => partnersList.sort((a, b) => sortAlphabetically(a.name, b.name, false, false)),
    autoSelectItem = false,
    translate = () => '',
}) => {
    const favouritePartnersResponse = useQuery(GET_USER_FAVOURITE_PARTNERS);
    const [updateUserFavouritePartner, updateUserFavouritePartnerResponse] = useMutation(UPDATE_USER_FAVOURITE_PARTNER);

    const onFavouritePartnerClicked = (e, isFavourite, id, name) => {
        e.stopPropagation();
        trackMixpanelEvent('Partners', isFavourite ? 'Unstar Partner' : 'Star Partner', { PartnerName: name });
        updateUserFavouritePartner({ variables: { partnerId: id, favourite: !isFavourite } });
    };

    useEffect(() => {
        const { called, loading, data } = updateUserFavouritePartnerResponse;

        if (!called || loading) {
            return;
        }

        if (data?.updateUserFavouritePartner?.ok) {
            favouritePartnersResponse.refetch();
        }

        updateUserFavouritePartnerResponse.reset();
    }, [updateUserFavouritePartnerResponse]);

    const formatContent = partner => (
        <div key={partner.id} className={css.partnerSummaryContent}>
            <Label
                text={`${translationPrefix}.APP_SITES_COUNT`}
                textReplace={{ count: partner.appSitesCount }}
                className={css.countLabel}
            />
            <Label
                text={`${translationPrefix}.LINKS_COUNT`}
                textReplace={{ count: partner.linksCount || translate(`${translationPrefix}.UNAVAILABLE`) }}
                className={css.countLabel}
            />
            <AgencyIndicator
                hasMissingAgencies={partner.missingAgenciesNames?.length > 0}
                hasAgency={partner.hasAgencyConfig}
                missingAgencyTooltipKey={`${translationPrefix}.MISSING_AGENCY_CONFIG`}
                hasAgencyTooltipKey={`${translationPrefix}.HAS_AGENCY_CONFIG`}
            />
        </div>
    );

    const formatLastUpdate = lastModified => {
        return `Last update ${moment(lastModified).format('MMM D, YYYY')}`;
    };

    const formattedPartners = partners.map(partner => ({
        ...partner,
        name: partner.displayName,
        content: formatContent(partner),
        lastUpdate: formatLastUpdate(partner.lastModified),
    }));

    const { data, loading: loadingFavourites } = favouritePartnersResponse;
    const { favouritePartners = null } = data || {};

    if (loadingFavourites) return null;

    return (
        <VerticalList
            selectedItem={selectedPartner}
            items={formattedPartners}
            filter={filter}
            className={className}
            onItemSelected={onPartnerSelected}
            sortItems={sortPartners}
            defaultIconElement={DefaultIcon}
            enableFavourites={favouritePartnersResponse.data !== undefined}
            favouriteItems={favouritePartners}
            onFavouriteItemClicked={onFavouritePartnerClicked}
            autoSelectItem={autoSelectItem}
            favouriteTooltip="STATIC.TOOLTIPS.MARK_FAVOURITE_PARTNER"
            disableAnimation
            iconClassName={css.partnerIcon}
        />
    );
};

PartnersList.propTypes = {
    partners: PropTypes.arrayOf(PropTypes.any),
    selectedPartner: PropTypes.number,
    filter: PropTypes.string,
    className: PropTypes.string,
    onPartnerSelected: PropTypes.func,
    sortPartners: PropTypes.func,
    autoSelectItem: PropTypes.bool,
    translate: PropTypes.func,
};

export default withLocalize(PartnersList);
